import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useTrans, useIsMobile } from 'hooks'
import { Button, Chip, Dropdown, ImageBox, SkeletonLoading } from 'components/atoms'
import { Page, ProdottiFiltri, ProdottiList } from 'components/organisms'
import { NotificationViewer, ProdottoOmaggioBox } from 'components/molecules'
import { Close, Filters } from 'components/atoms/Icons'
import SottocategorieList from 'components/organisms/SottocategorieList/SottocategorieList'
import { initializeApollo } from 'gql/client'
import { gql, useQuery } from '@apollo/client'
import { PREZZI_PRODOTTI, OMAGGIO } from 'utils/queries'
import { LISTING } from 'utils/endpoint'
import { api, isEmpty } from 'utils'
import { useRouter } from 'next/router'
import { getCategoriaSlug, setFiltriSlug, getFiltriSlug } from 'utils/safe'
import { trackingProductImpression, trackingGAevent } from 'utils/gtm'
import { useSelector } from 'react-redux'

interface Props {
  categoria: any
  pagina: any
  cantina?: {
    nome?: string
    logoTrasparente?: {
      image?: string
    }
    url?: string
  }
  sottocategorie: {
    voceMenu: string
    icon: any
    url: string
  }[]
  listing: any
  className?: string
}

const ProdottiListingTemplate = (props: Props) => {
  const {
    categoria = {},
    pagina = {},
    cantina = null,
    sottocategorie = [],
    listing = {},
    className = '',
  } = props

  const t = useTrans()
  const router = useRouter()

  const isMobile = useIsMobile()

  const [firstRender, setFirstRender] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingPrezzi, setLoadingPrezzi] = useState(true)
  const [filtriMobileOpen, setFiltriMobileOpen] = useState(false)
  const [omaggio, sePromoOmaggio] = useState(null)

  useEffect(() => {
    if (filtriMobileOpen) document.body.classList.add('w-noscroll')
    else document.body.classList.remove('w-noscroll')
  }, [filtriMobileOpen])

  const client = initializeApollo(null, null, null, router.locale)

  const [prodotti, setProdotti] = useState([])
  const [prodottiInfinite, setProdottiInfinite] = useState([])
  const [newProdotti, setNewProdotti] = useState([])
  const [numPages, setNumPages] = useState(null)
  const [page, setPage] = useState(null)
  const [notificationMessage, setNotificationMessage] = useState('')

  const [maxPrezzo, setMaxPrezzo] = useState()
  const [stickyActions, setStickyActions] = useState(false)
  const activateStickyActionsRef = useRef()
  const [firstStaticQuery, setFirstStaticQuery] = useState(true)

  const { menu } = useSelector((state: any) => state.menu)

  useEffect(() => {
    const onScroll = (e) => {
      setStickyActions(
        activateStickyActionsRef?.current &&
          e.target.documentElement.scrollTop > activateStickyActionsRef.current.offsetTop
      )
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (firstStaticQuery && router.query['o']) {
      setFirstStaticQuery(false)

      if (router.query['pmin']) rangeValues[0] = router.query['pmin']

      if (router.query['pmax']) rangeValues[1] = router.query['pmax']

      handleOrdinamento(router.query['o'])
    }
  }, [router.query])

  const attributiToFiltri = (attrs) => {
    return attrs.map((attr) => ({
      id: attr.id,
      chiave: attr.chiave,
      label: attr.nome,
      valori_disponibili: attr.valori_disponibili.map((v) => ({
        id: v.id,
        label: v.nome,
        slug: v.slug,
        selezionato: v.selezionato,
        disabilitato: v.disabilitato,
      })),
    }))
  }

  const [filtri, setFiltri] = useState([])

  const ordinamentiToFiltri = (ords, selected) => {
    return ords?.map((o) => ({
      label: o.placeholder,
      value: o.value,
      selected: o.value === selected,
    }))
  }

  const [ordinamenti, setOrdinamenti] = useState([])

  const handleOrdinamento = async (value) => {
    let newOrdinamenti = ordinamenti.slice()
    for (let i = 0; i < newOrdinamenti.length; i++)
      newOrdinamenti[i].selected = newOrdinamenti[i].value === value
    setOrdinamenti(newOrdinamenti)

    fetchListing()
  }

  const [rangeValues, setRangeValues] = useState([null, null])

  const onRangePrezziChange = (newRangeValues) => {
    setRangeValues(newRangeValues)
  }

  const onRangePrezziRelease = () => {
    if (isMobile) {
      return
    }

    trackingGAevent(
      6,
      'listing',
      'Fascia di prezzo',
      `${String(rangeValues[0])}-${String(rangeValues[1])}`
    )
    fetchListing()
  }

  const setPrezzi = async (listing, infiniteLoading = false) => {
    if (!infiniteLoading) setLoadingPrezzi(true)
    await loadPrezzi(listing.elementi).then((prezziProdotti) => {
      const newProdotti = listing.elementi.map((p) => {
        const current = prezziProdotti.find((x) => x.pk === p.pk)
        return { ...p, ...current }
      })
      if (infiniteLoading) {
        setProdottiInfinite(prodottiInfinite.concat(newProdotti))
        setNumPages(listing.paginator?.num_pages)
        setPage(listing.page)
      } else {
        if (prodottiInfinite.length) {
          setProdottiInfinite([])
        }
        setProdotti(newProdotti)
      }

      setNewProdotti(newProdotti)
      setLoadingPrezzi(false)
    })
  }

  const setListing = async (listing) => {
    setMaxPrezzo(listing.max_prezzo)
    setNumPages(listing.paginator?.num_pages)
    setPage(listing.page)
    setOrdinamenti(ordinamentiToFiltri(listing.ordinamenti, listing.ordine))
    setRangeValues([
      listing.prezzo_min_filtrato && listing.prezzo_min_filtrato <= listing.max_prezzo
        ? listing.prezzo_min_filtrato
        : null,
      listing.prezzo_max_filtrato && listing.prezzo_max_filtrato <= listing.max_prezzo
        ? listing.prezzo_max_filtrato
        : null,
    ])
    setFiltri(attributiToFiltri(listing.attributi))

    setProdotti(listing.elementi)
  }

  const fetchListing = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    } else {
      setLoadingMore(true)
    }

    const filtri_slug = filtri
      .map((attr) => attr.valori_disponibili.filter((v) => v.selezionato).map((v) => v.slug)) // array di array di selezionati
      .filter((attr) => attr.length > 0) // solo quelli con almento 1 valore selezionato
      .reduce((a, b) => a.concat(b), []) // unisco in array a 1 livello
      .join('/')

    const params = {
      id_categoria: categoria.pk,
      p: page,
      o: ordinamenti.find((o) => o.selected)?.value,
      filtri_slug: filtri_slug,
      lang: router.locale,
    }

    if (rangeValues[0]) {
      params['pmin'] = rangeValues[0]
    }
    if (rangeValues[1]) {
      params['pmax'] = rangeValues[1]
    }

    api.get(LISTING, { params }).then((res) => {
      const { listing } = res.data
      if (page === 1) {
        setListing(listing).then(() => {
          setLoading(false)
          setPrezzi(listing)
        })
      } else {
        setPrezzi(listing, true).then(() => {
          setLoadingMore(false)
        })
      }
    })

    router.query['o'] = ordinamenti.find((o) => o.selected)?.value

    if (rangeValues[0]) {
      router.query['pmin'] = rangeValues[0]
    }
    if (rangeValues[1]) {
      router.query['pmax'] = rangeValues[1]
    }

    router.query.slug = setFiltriSlug(getCategoriaSlug(router.query.slug), filtri_slug)

    router.push(
      {
        pathname: router.pathname,
        query: router.query,
      },
      undefined,
      { shallow: true }
    )
  }

  const loadPromoOmaggio = async () => {
    const data = await client.query({
      query: gql`
        ${OMAGGIO}
      `,
    })

    sePromoOmaggio(data.data.omaggio)
  }

  const loadPrezzi = async (prodotti) => {
    if (!prodotti || prodotti.length === 0) {
      return null
    }
    const idProdotti = prodotti.map((p) => p.pk)

    const data = await client.query({
      query: gql`
        ${PREZZI_PRODOTTI}
      `,
      variables: { idProdotti: idProdotti },
    })

    const prezziProdotti = data.data.prezziProdotti

    return prezziProdotti
  }

  const init = async () => {
    if (listing && listing.elementi) {
      loadPromoOmaggio()
      await setListing(listing).then(() => {
        setPrezzi(listing)
        setFirstRender(false)
      })
    }
  }

  useEffect(() => {
    if (listing) {
      init()
    }
  }, [listing])

  const handleFilters = async (action, attributoId, valoreId) => {
    let newFiltri = filtri.slice()
    let attributoIndex = newFiltri.findIndex((attributo) => attributo.id === attributoId)
    if (attributoIndex > -1) {
      let valueIndex = newFiltri[attributoIndex].valori_disponibili.findIndex(
        (valore) => valore.id === valoreId
      )
      if (valueIndex > -1) {
        switch (action) {
          case 'remove':
            newFiltri[attributoIndex].valori_disponibili[valueIndex].selezionato = false
            break
          case 'toggle':
            newFiltri[attributoIndex].valori_disponibili[valueIndex].selezionato =
              !newFiltri[attributoIndex].valori_disponibili[valueIndex].selezionato
            break
        }
      }
    }
    setFiltri(newFiltri)
    if (!isMobile || action === 'remove') {
      fetchListing()
    }
  }

  const handleResetFilters = () => {
    let newFiltri = filtri.slice()
    for (let attributoIndex = 0; attributoIndex < filtri.length; attributoIndex++) {
      for (
        let valoreIndex = 0;
        valoreIndex < filtri[attributoIndex].valori_disponibili.length;
        valoreIndex++
      ) {
        if (filtri[attributoIndex].valori_disponibili[valoreIndex].selezionato) {
          newFiltri[attributoIndex].valori_disponibili[valoreIndex].selezionato = false
        }
      }
    }
    setFiltri(newFiltri)

    fetchListing()
  }

  const handleSetFilters = () => {
    if (!isMobile) {
      return
    }

    setFiltriMobileOpen(false)
    fetchListing()
  }

  const handleMoreProducts = () => {
    if (page < numPages) {
      fetchListing(page + 1)
    }
  }

  const handleNotificationMessage = (message) => {
    setNotificationMessage(message)
  }

  const onCloseNotification = () => {
    setNotificationMessage('')
  }

  const isPageOfferta = () => {
    if (menu && !isEmpty(menu)) {
      const mainMenu = menu?.edges.find((item) => item.node.key === 'main')
      const offerte = mainMenu?.node.children.edges.find((item) => item.node.key === 'offerte')
        ?.node?.children?.edges
      const isOfferta = offerte.some(
        (item) => item.node.url === '/i-vini/' + categoria?.slugTree + '/'
      )
      return isOfferta
    }
    return false
  }

  useEffect(() => {
    if (notificationMessage && notificationMessage !== '') {
      setTimeout(() => {
        setNotificationMessage('')
      }, 5000)
    }
  }, [notificationMessage])

  useEffect(() => {
    if (newProdotti.length > 0)
      trackingProductImpression(
        newProdotti,
        isPageOfferta() ? 'Category Offerte' : 'Category Listing',
        prodotti.length + prodottiInfinite.length - newProdotti.length
      )
  }, [newProdotti])

  return (
    <Page
      pagina={pagina}
      item={categoria}
      hideDescrizione={true}
      customContent={
        cantina ? (
          <>
            <div className="prodotti-listing-template__head">
              <div className="prodotti-listing-template__head__content">
                <p className="prodotti-listing-template__intro">{t('I prodotti della cantina')}</p>
                <p className="page__title">{categoria.nome}</p>
                <div
                  className="page__description"
                  dangerouslySetInnerHTML={{ __html: categoria?.descrizione }}
                ></div>
                <Button
                  label={t('Scopri la cantina')}
                  variant="ghost"
                  href={cantina.url}
                  className="prodotti-listing-template__head__cta"
                />
              </div>
              <Link legacyBehavior href={cantina.url}>
                <a>
                  <ImageBox
                    src={cantina.logoTrasparente?.image}
                    maxWidth={176}
                    maxHeight={176}
                    classPrefix="prodotti-listing-template__head"
                  />
                </a>
              </Link>
            </div>
          </>
        ) : null
      }
      className={`prodotti-listing-template ${
        stickyActions ? ' prodotti-listing-template--sticky-actions' : ''
      } ${className ? ` ${className}` : ''}`}
    >
      <NotificationViewer
        showError={notificationMessage && notificationMessage !== ''}
        messageError={notificationMessage}
        messageSuccess="Il prodotto è stato aggiunto al carrello"
        onClose={() => onCloseNotification()}
      />
      {sottocategorie.length > 0 && (
        <SottocategorieList
          sottocategorie={sottocategorie}
          className="prodotti-listing-template__sottocategorie"
        />
      )}
      <div className="prodotti-listing-template__layout">
        {filtriMobileOpen && (
          <div
            className="prodotti-listing-filtri-overlay"
            onClick={() => setFiltriMobileOpen(false)}
          />
        )}
        <div
          className={`prodotti-listing-template__filtri${
            filtriMobileOpen ? ' prodotti-listing-template__filtri--open' : ''
          }`}
        >
          <div className="prodotti-listing-template__filtri__head-mobile">
            <button
              type="button"
              className="prodotti-listing-template__filtri__close"
              onClick={() => setFiltriMobileOpen(false)}
            >
              {<Close />}
            </button>
            <Button
              label={t('Azzera filtri')}
              variant="ghost"
              colorVariant="black"
              onClick={handleResetFilters}
            />
          </div>
          <div className="prodotti-listing-template__filtri__list">
            <div className="prodotti-listing-template__filtri__filtri-attivi">
              <p className="prodotti-listing-template__filtri__filtri-attivi__title">
                {t('Filtri attivi')}
              </p>
              <div className="prodotti-listing-template__filtri__filtri-attivi__list">
                {filtri.map(
                  (filtro) =>
                    filtro.valori_disponibili.length > 1 &&
                    filtro.valori_disponibili.map((valore) =>
                      valore.selezionato ? (
                        <Chip
                          label={valore.label}
                          removable
                          onClick={() => handleFilters('remove', filtro.id, valore.id)}
                          key={`prodotti-listing-template__filtri__filtri-attivi__list__item__${valore.id}`}
                        />
                      ) : (
                        <></>
                      )
                    )
                )}
              </div>
            </div>
            <ProdottiFiltri
              filtri={filtri}
              onValoreToggle={(attributoId, valoreId) =>
                handleFilters('toggle', attributoId, valoreId)
              }
              maxPrice={maxPrezzo}
              minRangePrice={rangeValues[0]}
              maxRangePrice={rangeValues[1]}
              onRangePrezziChange={onRangePrezziChange}
              onRangePrezziRelease={onRangePrezziRelease}
            />
          </div>
          <div className="prodotti-listing-template__filtri__footer-mobile">
            <Button label={t('Applica filtri')} onClick={handleSetFilters} />
          </div>
        </div>
        <div className="prodotti-listing-template__main">
          <div className="prodotti-listing-template__options">
            <div className="prodotti-listing-template__filtri-attivi">
              <div className="prodotti-listing-template__filtri-attivi__slider">
                <Swiper spaceBetween={6} slidesPerView="auto">
                  {filtri.map(
                    (filtro) =>
                      filtro.valori_disponibili.length > 1 &&
                      filtro.valori_disponibili.map((valore) =>
                        valore.selezionato ? (
                          <SwiperSlide
                            key={`prodotti-listing-template__filtri-attivi-slider__item__-${valore.id}`}
                          >
                            <Chip
                              label={valore.label}
                              removable
                              onClick={() => handleFilters('remove', filtro.id, valore.id)}
                            />
                          </SwiperSlide>
                        ) : (
                          <></>
                        )
                      )
                  )}
                </Swiper>
              </div>
            </div>
            <div className="prodotti-listing-template__actions">
              <div className="prodotti-listing-template__open-filtri">
                <Button
                  label={t('Mostra filtri')}
                  icon={<Filters />}
                  iconPos="left"
                  variant="ghost"
                  colorVariant="black"
                  onClick={() => setFiltriMobileOpen(true)}
                />
              </div>
              <div className="prodotti-listing-template__order">
                <Dropdown
                  label={t('Ordina per')}
                  options={ordinamenti}
                  onChange={handleOrdinamento}
                />
              </div>
            </div>
          </div>
          <div ref={activateStickyActionsRef} />
          {loading ? (
            <div className="prodotti-list-loading">
              {Array(isMobile ? 6 : 9)
                .fill('')
                .map((_, i) => (
                  <SkeletonLoading vista2={isMobile} key={i} />
                ))}
            </div>
          ) : (
            <>
              {/* 
                al primo rendering, prodotti è lista vuota, listing è popolata (server-side) 
                ai successivi, è sempre usato prodotti
              */}

              <div className="prodotto-main__omaggio-box">
                {omaggio?.prodottoOmaggio && (
                  <ProdottoOmaggioBox
                    title={omaggio.nome}
                    description={omaggio.descrizione}
                    imageUrl={omaggio.prodottoOmaggio.prodotto.mainImage}
                    className="listing"
                  />
                )}
              </div>

              {prodotti && !firstRender ? (
                <ProdottiList
                  prodotti={prodotti}
                  loadingPrezzi={loadingPrezzi}
                  onNotificationMessage={(message) => handleNotificationMessage(message)}
                  sezione={isPageOfferta() ? 'Category Offerte' : 'Category Listing'}
                />
              ) : listing ? (
                <ProdottiList
                  prodotti={listing.elementi}
                  loadingPrezzi={loadingPrezzi}
                  onNotificationMessage={(message) => handleNotificationMessage(message)}
                  sezione={isPageOfferta() ? 'Category Offerte' : 'Category Listing'}
                />
              ) : null}

              {prodottiInfinite && (
                <ProdottiList
                  prodotti={prodottiInfinite}
                  showEmpty={true}
                  sezione={isPageOfferta() ? 'Category Offerte' : 'Category Listing'}
                  posizioneStart={prodotti.length}
                />
              )}
            </>
          )}
          {!loading && page < numPages && (
            <div className="prodotti-listing-template__more">
              <Button
                label={t('Carica altri')}
                variant="secondary"
                onClick={handleMoreProducts}
                loading={loadingMore}
              />
            </div>
          )}
          {!cantina && categoria.descrizione && (
            <div
              className="prodotti-listing-template__description"
              dangerouslySetInnerHTML={{ __html: categoria.descrizione }}
            />
          )}
        </div>
      </div>
    </Page>
  )
}

export default ProdottiListingTemplate
